/**
 * Tenants (tenant_ownerlar) API. Normalizes snake_case to camelCase.
 */
import api from './api';
import { normalizeSaleUnit } from '../utils/productSaleUnit';
import { endpoints } from './endpoints';

const STATUSES = ['active', 'suspended', 'trial', 'payment_pending'];

function normalizeTenant(raw) {
  if (!raw) return null;
  const regionVal = raw.region ?? '';
  const regionObj = typeof regionVal === 'object' ? regionVal : null;
  const regionCode = regionObj ? null : String(regionVal);
  const regionId = String(raw.region_id ?? raw.regionId ?? (regionObj?.id ?? regionCode) ?? '');
  const regionName = regionObj?.name_uz ?? regionObj?.name_ru ?? regionObj?.name ?? regionCode ?? '';
  const districtVal = raw.district ?? raw.district_id;
  const districtObj = typeof districtVal === 'object' ? districtVal : null;
  const districtName = districtObj?.name_uz ?? districtObj?.name_ru ?? districtObj?.name ?? raw.district_name ?? raw.districtName ?? '';
  return {
    id: String(raw.id),
    companyName: raw.company_name ?? raw.companyName ?? '',
    businessTypeId: String(raw.business_type_id ?? raw.businessTypeId ?? ''),
    businessTypeName: raw.business_type_name ?? raw.businessTypeName ?? '',
    contactName: raw.contact_name ?? raw.contactName ?? '',
    phone: raw.phone ?? '',
    planKey: raw.plan_key ?? raw.planKey ?? '',
    status: STATUSES.includes(raw.status) ? raw.status : 'active',
    regionId,
    region: regionName || regionCode,
    districtId: String(raw.district_id ?? raw.districtId ?? (districtObj?.id ?? '') ?? ''),
    districtName: districtName || '',
    cashiersCount: Number(raw.cashiers_count ?? raw.cashiersCount ?? raw.users_count ?? raw.usersCount ?? 0),
    createdAt: raw.created_at ?? raw.createdAt ?? '',
    note: raw.note ?? '',
    address: raw.address != null ? String(raw.address) : '',
    trialEndsAt: raw.trial_ends_at ?? raw.trialEndsAt ?? null,
    subscriptionEndsAt: raw.subscription_ends_at ?? raw.subscriptionEndsAt ?? null,
    cancellationRequestedAt: raw.cancellation_requested_at ?? raw.cancellationRequestedAt ?? null,
    blockedAt: raw.blocked_at ?? raw.blockedAt ?? null,
    blockReason: raw.block_reason ?? raw.blockReason ?? null,
    loginSms2faEnabled: Boolean(raw.login_sms_2fa_enabled ?? raw.loginSms2faEnabled ?? false),
    cashbackBalanceUzs: Number(raw.cashback_balance_uzs ?? raw.cashbackBalanceUzs ?? 0) || 0,
    cashbackLastRedemptionAt: raw.cashback_last_redemption_at ?? raw.cashbackLastRedemptionAt ?? null,
  };
}

export async function getTenants(params) {
  const merged = { per_page: 200, page: 1, ...(params || {}) };
  const res = await api.get(endpoints.tenants, { params: merged });
  /** Laravel: { data: [...], meta } yoki ba'zan { data: { data: [...], meta } } */
  let payload = res?.data ?? res;
  if (payload && !Array.isArray(payload) && Array.isArray(payload.data)) {
    payload = payload.data;
  }
  const list = Array.isArray(payload) ? payload : [];
  return list.map(normalizeTenant).filter(Boolean);
}

function normalizeStats(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    ordersCount: Number(raw.orders_count ?? raw.ordersCount ?? raw.total_orders ?? raw.totalOrders ?? 0),
    ordersMonth: Number(raw.orders_month ?? raw.ordersMonth ?? raw.monthly_orders ?? raw.monthlyOrders ?? 0),
    revenueMonth: Number(raw.revenue_month ?? raw.revenueMonth ?? raw.revenue_monthly ?? raw.monthly_revenue ?? 0),
    revenueTotal: Number(raw.revenue_total ?? raw.revenueTotal ?? raw.total_revenue ?? 0),
    branchesCount: Number(raw.branches_count ?? raw.branchesCount ?? raw.branches ?? 0),
    cashiersCount: Number(raw.cashiers_count ?? raw.cashiersCount ?? 0),
    lastOrderAt: raw.last_order_at ?? raw.lastOrderAt ?? raw.last_order ?? null,
    activeSince: raw.active_since ?? raw.activeSince ?? null,
  };
}

function normalizeProduct(p) {
  if (!p || typeof p !== 'object') return null;
  const name = p.name ?? p.product_name ?? p.title ?? String(p.id ?? '—');
  const stockVal = p.stock ?? p.quantity ?? p.current_stock ?? p.quantity_available ?? p.stock_quantity ?? p.qty ?? p.remaining ?? p.balance ?? 0;
  const soldVal = p.sold_count ?? p.sold ?? p.soldCount ?? p.quantity_sold ?? p.sold_quantity ?? p.total_sold ?? p.qty_sold ?? 0;
  const purchasePrice = p.purchase_price ?? p.cost_price ?? p.price_buy ?? p.buy_price ?? p.price_purchase ?? p.purchasePrice ?? 0;
  const salePrice = p.sale_price ?? p.price_sell ?? p.sell_price ?? p.price ?? p.salePrice ?? 0;
  const expiryRaw =
    p.expiry_date ??
    p.expiryDate ??
    p.expires_at ??
    p.expiresAt ??
    p.expiration_date ??
    p.expirationDate ??
    p.valid_until ??
    p.validUntil ??
    null;
  return {
    id: p.id,
    name: String(name),
    status: p.status ?? 'active',
    soldCount: Number(soldVal),
    stock: Number(stockVal),
    quantity: Number(stockVal || (p.quantity ?? 0)),
    purchasePrice: Number(purchasePrice),
    salePrice: Number(salePrice),
    saleUnit: normalizeSaleUnit(
      p.sale_unit ?? p.saleUnit ?? p.unit_type ?? p.unitType ?? p.measure_unit ?? p.measureUnit
    ),
    expiryDate: expiryRaw != null && String(expiryRaw).trim() !== '' ? String(expiryRaw).trim().slice(0, 10) : null,
  };
}

function normalizeProducts(raw) {
  const arr = raw?.products ?? raw?.products_list ?? raw?.product_summary?.products ?? raw?.product_summary?.items;
  if (!Array.isArray(arr)) return [];
  return arr.map(normalizeProduct).filter(Boolean);
}

export async function getTenantDetail(id) {
  const res = await api.get(endpoints.tenantById(id));
  const raw = res?.data ?? res;
  if (!raw) return null;
  const tenant = normalizeTenant(raw);
  const stats = normalizeStats(raw.stats ?? raw.statistics ?? raw);
  let products = normalizeProducts(raw);
  if (!products?.length && endpoints.tenantProducts) {
    try {
      const prodRes = await api.get(endpoints.tenantProducts(id));
      const prodRaw = prodRes?.data ?? prodRes;
      products = Array.isArray(prodRaw) ? prodRaw.map(normalizeProduct).filter(Boolean) : normalizeProducts(prodRaw);
    } catch {
      products = [];
    }
  }
  return { ...tenant, stats, products: products ?? [] };
}

export async function getTenantReceiptLayout(id) {
  if (!id) return null;
  const res = await api.get(endpoints.tenantReceiptLayout(id), { retries: 1 });
  return res?.data ?? res ?? null;
}

export async function updateTenantReceiptLayout(id, layout) {
  if (!id) return null;
  const res = await api.put(endpoints.tenantReceiptLayout(id), layout, { retries: 1 });
  return res?.data ?? res ?? null;
}

/**
 * Mijozni faqat asosiy sayt buyurtmasi bilan yaratish (dispatcher).
 * @param {{ orderId: string|number, address: string, ownerPassword: string, companyName?: string, contactName?: string, phone?: string, regionId?: string, districtId?: string, managerUserId?: string, businessTypeId?: string, planKey?: string, note?: string }} payload
 */
export async function createTenantFromOrder(payload) {
  const pwd =
    payload.ownerPassword != null && String(payload.ownerPassword).trim() !== ''
      ? String(payload.ownerPassword).trim()
      : '';
  const buildBody = ({ omitPhone = false } = {}) => {
    const body = {
      order_id: Number(payload.orderId),
      address: payload.address != null ? String(payload.address).trim() : '',
    };
    if (payload.companyName) body.company_name = String(payload.companyName);
    if (payload.contactName) body.contact_name = String(payload.contactName);
    if (!omitPhone && payload.phone) body.phone = String(payload.phone);
    if (omitPhone) body.omit_phone = 1;
    if (payload.regionId) body.region_id = Number(payload.regionId);
    if (payload.districtId) body.district_id = Number(payload.districtId);
    if (payload.managerUserId) body.manager_user_id = Number(payload.managerUserId);
    if (payload.businessTypeId) body.business_type_id = Number(payload.businessTypeId);
    if (payload.note) body.note = String(payload.note);
    if (pwd) {
      body.password = pwd;
      body.owner_password = pwd;
    }
    return body;
  };

  try {
    const { data } = await api.post(endpoints.tenants, buildBody({ omitPhone: false }));
    return normalizeTenant(data?.data ?? data);
  } catch (e) {
    const err = e;
    const status = Number(err?.status ?? err?.response?.status ?? 0);
    const code = err?.data?.code ?? err?.data?.meta?.code ?? null;
    // Telefon dubl bo'lsa — tenantni telefonsiz yaratib, jarayonni to'xtatmaymiz.
    if (status === 422 && String(code || '').toUpperCase() === 'PHONE_ALREADY_EXISTS') {
      const { data } = await api.post(endpoints.tenants, buildBody({ omitPhone: true }));
      return normalizeTenant(data?.data ?? data);
    }
    throw err;
  }
}

export async function updateTenant(id, payload) {
  const body = {};
  if (payload.companyName !== undefined) body.company_name = payload.companyName;
  if (payload.businessTypeId !== undefined) body.business_type_id = payload.businessTypeId || null;
  if (payload.contactName !== undefined) body.contact_name = payload.contactName;
  if (payload.phone !== undefined) body.phone = payload.phone;
  if (payload.planKey !== undefined) {
    const pk = String(payload.planKey ?? '').trim();
    if (pk) body.plan_key = pk;
  }
  if (payload.status !== undefined) body.status = payload.status;
  if (payload.regionId !== undefined) body.region_id = payload.regionId || null;
  if (payload.districtId !== undefined) body.district_id = payload.districtId || null;
  if (payload.cashiersCount !== undefined) body.cashiers_count = payload.cashiersCount;
  if (payload.note !== undefined) body.note = payload.note;
  if (payload.address !== undefined) body.address = payload.address != null ? String(payload.address).trim() : '';
  if (payload.loginSms2faEnabled !== undefined) {
    body.login_sms_2fa_enabled = Boolean(payload.loginSms2faEnabled);
  }
  const pwd =
    payload.ownerPassword != null && String(payload.ownerPassword).trim() !== ''
      ? String(payload.ownerPassword).trim()
      : '';
  if (pwd) {
    body.password = pwd;
    body.owner_password = pwd;
  }
  const { data } = await api.put(endpoints.tenantById(id), body);
  return normalizeTenant(data?.data ?? data);
}

export async function deleteTenant(id) {
  await api.delete(endpoints.tenantById(id));
}

/** Mijoz: obunani bekor qilish so‘rovi */
export async function postTenantSubscriptionCancelRequest(note) {
  const body = note != null && String(note).trim() !== '' ? { note: String(note).trim() } : {};
  const { data } = await api.post(endpoints.tenantSubscriptionCancelRequest, body);
  return normalizeTenant(data?.data ?? data);
}

/** Mijoz: tarif rejasini o'zgartirish so'rovi (platforma menejerlariga log/transaction orqali). */
export async function postTenantSubscriptionPlanChangeRequest(payload = {}) {
  const body = {};
  if (payload.desiredPlanKey != null && String(payload.desiredPlanKey).trim() !== '') {
    body.desired_plan_key = String(payload.desiredPlanKey).trim();
  }
  if (payload.note != null && String(payload.note).trim() !== '') {
    body.note = String(payload.note).trim();
  }
  const { data } = await api.post(endpoints.tenantSubscriptionPlanChangeRequest, body);
  return normalizeTenant(data?.data ?? data);
}

/** Obuna tranzaksiyalari (mijoz o‘zi yoki menejerlar) */
export async function getTenantSubscriptionTransactions(tenantId, params) {
  try {
    const { data } = await api.get(endpoints.tenantSubscriptionTransactions(tenantId), { params: params || {} });
    return Array.isArray(data?.data) ? data.data : [];
  } catch (e) {
    const status = Number(e?.status ?? e?.response?.status ?? 0);
    // Backendda bu endpoint yo'q bo'lishi mumkin (eski frontend qoldig'i).
    if (status === 404) return [];
    throw e;
  }
}
